import React from "react";
import { Drawer, Form, Input, Select, Switch, Button, Space, Upload, message, Modal, Typography } from "antd";
import { DeleteOutlined, UploadOutlined } from "@ant-design/icons";
import { createCampaign, updateCampaign, uploadCampaignImage } from "services/reward-campaign-service";

const { Option } = Select;
const { Dragger } = Upload;
const { Text } = Typography;

const ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/jpg"];
const MAX_SIZE_MB = 1;

const RewardCampaignForm = ({ visible, campaign, onClose }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);
  const [logoUrl, setLogoUrl] = React.useState(null);
  const [previewUrl, setPreviewUrl] = React.useState(null);
  const [pendingFile, setPendingFile] = React.useState(null);
  const isEdit = !!campaign;

  React.useEffect(() => {
    if (visible) {
      if (campaign) {
        form.setFieldsValue({
          name: campaign.name,
          redemptionType: campaign.redemptionType?.code,
          statusActive: campaign.status?.code === "ACTIVE",
        });
        setLogoUrl(campaign.logoUrl || null);
      } else {
        form.resetFields();
        form.setFieldsValue({ statusActive: true });
        setLogoUrl(null);
      }
      setPendingFile(null);
      setPreviewUrl(null);
    } else {
      setPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      setPendingFile(null);
    }
  }, [visible, campaign]);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      let finalLogoUrl = logoUrl;
      if (pendingFile) {
        const data = await uploadCampaignImage(pendingFile);
        finalLogoUrl = data.url;
      }
      const payload = {
        name: values.name,
        logoUrl: finalLogoUrl ?? "",
        redemptionType: values.redemptionType,
        status: values.statusActive ? "ACTIVE" : "INACTIVE",
      };
      if (isEdit) {
        await updateCampaign(campaign.id, payload);
        message.success("Campanha atualizada com sucesso");
      } else {
        await createCampaign(payload);
        message.success("Campanha criada com sucesso");
      }
      onClose(true);
    } catch (e) {
      message.error("Erro ao salvar campanha");
    } finally {
      setLoading(false);
    }
  };

  const beforeUpload = (file) => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      message.error("Formato inválido. Use PNG, JPG ou JPEG.");
      return Upload.LIST_IGNORE;
    }
    if (file.size / 1024 / 1024 > MAX_SIZE_MB) {
      message.error(`Imagem muito grande. Tamanho máximo: ${MAX_SIZE_MB}MB.`);
      return Upload.LIST_IGNORE;
    }
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
    setPendingFile(file);
    return false;
  };

  const handleRemoveLogo = () => {
    Modal.confirm({
      title: "Remover logo",
      content: "Tem certeza que deseja remover a logo desta campanha?",
      okText: "Remover",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: () => {
        setPreviewUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return null;
        });
        setLogoUrl(null);
        setPendingFile(null);
      },
    });
  };

  const displayUrl = previewUrl || logoUrl;

  return (
    <Drawer
      title={isEdit ? "Editar Campanha" : "Nova Campanha"}
      open={visible}
      onClose={() => onClose(false)}
      width={480}
      placement="right"
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Space>
            <Button onClick={() => onClose(false)}>Cancelar</Button>
            <Button type="primary" loading={loading} onClick={() => form.submit()}>
              {isEdit ? "Salvar" : "Criar"}
            </Button>
          </Space>
        </div>
      }
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Form.Item
          name="name"
          label="Nome da Campanha"
          rules={[
            { required: true, message: "Nome é obrigatório" },
            { max: 50, message: "Nome deve ter no máximo 50 caracteres" },
          ]}
        >
          <Input placeholder="Ex.: Verao 2026" maxLength={50} />
        </Form.Item>

        <Form.Item label="Logo da Campanha">
          {displayUrl ? (
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
              <img
                src={displayUrl}
                alt="Logo da campanha"
                style={{ width: 64, height: 64, objectFit: "contain", border: "1px solid #f0f0f0", borderRadius: 4 }}
              />
              <Button
                icon={<DeleteOutlined />}
                size="small"
                danger
                disabled={loading}
                onClick={handleRemoveLogo}
              >
                Remover
              </Button>
            </div>
          ) : (
            <>
              <ul style={{ paddingLeft: 16, marginBottom: 16, fontSize: 13, color: "rgba(0,0,0,0.45)" }}>
                <li>Tamanho limite: 1MB</li>
                <li>Formatos aceitos: PNG, JPEG e JPG</li>
                <li>Dimensões sugeridas: 200×200 até 400×400px</li>
                <li>Toda comunicação do Link+ segue o fundo branco, logo solicitamos que seu logo seja escuro ou que contraste com o branco</li>
              </ul>
              <Dragger
                accept=".png,.jpg,.jpeg"
                maxCount={1}
                showUploadList={false}
                beforeUpload={beforeUpload}
              >
                <UploadOutlined style={{ fontSize:'1.5rem' }} />
                <p>Clique aqui ou arraste sua imagem para fazer o upload</p>
              </Dragger>
            </>
          )}
          <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 4 }}>
            Deixe em branco para usar a logo padrão configurada na empresa
          </Text>
        </Form.Item>

        <Form.Item
          name="redemptionType"
          label="Tipo de Resgate"
          initialValue="PIX"
          rules={[{ required: true, message: "Selecione o tipo de resgate" }]}
        >
          <Select placeholder="Selecione">
            <Option value="PIX">PIX</Option>
            {/*
              //TODO: REWARD VOUCHER
              <Option value="CARD">Cartão Virtual</Option>
            */}
          </Select>
        </Form.Item>
        <Form.Item name="statusActive" label="Status" valuePropName="checked">
          <Switch checkedChildren="Ativo" unCheckedChildren="Inativo" />
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default RewardCampaignForm;
